import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '~/components/system/form-field';
import { serverMessage } from '~/lib/api/server-error';
import { useFilterTemplateMutations } from '~/hooks/use-filter-templates';

/** Same limit as the template edit form (`filterTemplateSchema`). */
const MAX_NAME_LENGTH = 100;

interface Props {
	/** Template being copied; the dialog is open while it is set. */
	source: { id: number; nm: string } | null;
	onClose: () => void;
}

/**
 * Copy a filter template (Πρότυπο Φίλτρων) with all its filters. The copy has no categories: the source keeps its own,
 * and a category is pointed at the copy afterwards from its «Πρότυπο φίλτρων» field. The server lists the copy right
 * under its source.
 */
export function FilterTemplateCopyDialog({ source, onClose }: Props) {
	const { t } = useTranslation();
	const { copy } = useFilterTemplateMutations();
	const [name, setName] = useState('');
	const [serverError, setServerError] = useState<string | null>(null);

	useEffect(() => {
		if (!source) return;
		setName(`${source.nm} - ${t('catalog.filterTemplates.copyNameSuffix')}`);
		setServerError(null);
	}, [source, t]);

	const trimmed = name.trim();
	let nameError: string | null = null;
	if (!trimmed) nameError = 'validation.required';
	else if (trimmed.length > MAX_NAME_LENGTH) nameError = 'validation.tooLong';

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!source || nameError) return;
		setServerError(null);
		try {
			await copy.mutateAsync({ id: source.id, nm: trimmed });
			toast.success(t('catalog.filterTemplates.copied'));
			onClose();
		} catch (err) {
			// The refusal that matters here is a taken name, so it is shown on the name field.
			setServerError(serverMessage(err));
		}
	};

	return (
		// Not closable while saving, so a late error cannot land in a dialog opened for another template.
		<Dialog open={Boolean(source)} onOpenChange={open => !open && !copy.isPending && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t('catalog.filterTemplates.copyTitle', { name: source?.nm ?? '' })}</DialogTitle>
					<DialogDescription>{t('catalog.filterTemplates.copyBody')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('catalog.filterTemplates.columns.name')} error={nameError ?? serverError ?? undefined}>
						<Input
							value={name}
							onChange={e => {
								setName(e.target.value);
								setServerError(null);
							}}
							aria-invalid={Boolean(nameError ?? serverError)}
							autoFocus
						/>
					</FormField>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={onClose} disabled={copy.isPending}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={copy.isPending || Boolean(nameError)}>
							{copy.isPending ? t('common.saving') : t('catalog.filterTemplates.copy')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
